import {DrawerFooter} from "@/widget/DrawerModal/DrawerModal";
import TextField from "@mui/material/TextField";
import {Controller, useForm, useWatch} from "react-hook-form";
import {Autocomplete, FormLabel, Grid} from "@mui/material";
import FormControl from "@mui/material/FormControl";
import React from "react"
import Box from "@mui/material/Box";
import {useAllDict} from "@/store/dict.c";
import {DictPO} from "@/model/system";
import {useDrawerModal, useGlobalActions} from "@/store/global.c";
import {SearchData} from "@/widget/SearchData/SearchData";
import {CurrencyYuanOutlined} from '@mui/icons-material';
import {DatePickers} from "@/theme/DatePickers";
import DatePicker from "react-datepicker";
import {useCreateChance, useQueryChance, useUpdateChance} from "@/service/chance.s";
import {GroupProduct, GroupProductType} from "@/widget/GroupProduct/GroupProduct";
import {CustomerPO} from "@/model/customer";
import {ShowChartOutlined} from "@mui/icons-material";
import * as z from "zod";
import {zodResolver} from "@hookform/resolvers/zod";
import {checkQueryKey, emptyValue, iso2Date, setValues} from "@/helper/method";
import isEmpty from "lodash/isEmpty";

const validationSchema = z.object({
    id: z.number().nullable().default(0),
    customerId: z.number({message: "请选择客户名称"}),
    customerName: z.string().nullable(),
    stageId: z.number().nullable(),
    stage: z.string({message: "请选择商机阶段"}),
    kindId: z.unknown(),
    kind: z.unknown(),
    categoryId: z.unknown(),
    category: z.string({message: "请选择服务类型"}).nonempty({message: "请选择服务类型"}),
    productTypeId: z.unknown(),
    productType: z.unknown(),
    productIds: z.string({message: "请选择服务名称"}),
    productNames: z.string().nullable(),
    money: z.string({message: "请填写商机金额"}).nonempty({message: "请填写商机金额"}),
    amount: z.number({message: "请填写商机数量"}),
    dealDate: z
        .date({
            message: "请选择预计成交时间"
        }),
    dealRate: z.number({message: "请选择成交概率"}).max(100, "成交概率不能高于100"),
    remark: z.string().nonempty()
});

export type WriteChanceForm = z.infer<typeof validationSchema>

const defaultValues = emptyValue(validationSchema)

export const WriteChance = () => {
    const {stage, patentType, projectType, trademarkType, copyrightType} = useAllDict()
    const {write, id: drawerId} = useDrawerModal()
    const {setToast} = useGlobalActions()
    const {data: chanceData} = useQueryChance({id: drawerId!}, checkQueryKey(drawerId))
    const {mutateAsync: createChanceMutate, isPending: createChancePending} = useCreateChance()
    const {mutateAsync: updateChanceMutate, isPending: updateChancePending} = useUpdateChance()
    const chance = chanceData?.data
    const {onSearch} = useDrawerModal()
    const {setDrawerModal} = useGlobalActions()

    const initValues = () => {
        if (write == 'edit') {
            return {
                ...chance, dealDate: iso2Date(chance?.dealDate), productIds: `${chance?.productTypeId},${chance?.kindId}`,
                productNames: `${chance?.productType},${chance?.kind}`
            };
        }
    }
    const {
        register,
        control,
        setValue,
        reset,
        handleSubmit,
        formState: {errors},
    } = useForm<WriteChanceForm>({
        resolver: zodResolver(validationSchema),
        values: initValues() as WriteChanceForm,
        defaultValues: defaultValues,
    });

    const onSubmit = async (values: WriteChanceForm) => {
        const result = write == 'add' ? await createChanceMutate(values) : await updateChanceMutate({...values, id: drawerId!})
        if (result.code == 0) {
            onSearch && onSearch()
            setDrawerModal({open: false})
        } else {
            setToast({open: true, message: result.msg, severity: "error", variant: 'standard'})
        }
    }

    const CategoryWatched = () => {
        const productTypeId = useWatch({
            name: 'productTypeId',
            control
        })
        if (productTypeId == 5) {
            return <></>
        }
        let applyType: DictPO[] = []
        if (productTypeId == 1) {
            applyType = patentType
        } else if (productTypeId == 2) {
            applyType = projectType
        } else if (productTypeId == 3) {
            applyType = trademarkType
        } else if (productTypeId == 4) {
            applyType = copyrightType
        }
        return <Grid size={6}>
            <FormControl fullWidth={true} required={true}>
                <FormLabel>服务类型</FormLabel>
                <Controller control={control} render={({field: {onChange, value}}) => <Autocomplete
                    options={applyType || []}
                    value={value as any}
                    onChange={(_, option: DictPO | null) => {
                        setValue('categoryId', option?.value);
                        onChange(option?.label)
                    }}
                    fullWidth
                    renderInput={(params) => (
                        <TextField error={!!errors?.category}
                                   helperText={errors?.category?.message}
                                   variant="outlined"
                                   placeholder={"选择服务类型"} {...params} />
                    )}
                />} name={`category`}/>
            </FormControl>
        </Grid>
    }

    return (
        <Box component={'form'} noValidate onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
                <Grid size={6}>
                    <SearchData<WriteChanceForm, CustomerPO> errMsg={errors?.customerId?.message} control={control} attr={"customerName"} attrId={"customerId"} setValue={setValue}
                                                             label={"客户名称"}/>
                </Grid>
                <Grid size={6}>
                    <GroupProduct<GroupProductType> label={'服务名称'} onSelect={(item) => {
                        if (!isEmpty(item)) {
                            const values = item?.value?.split(',')
                            setValues({productType: item?.group, productTypeId: parseInt(values[0]), kind: item?.label, kindId: parseInt(values[1])}, setValue)
                        } else {
                            setValues({productType: null, productTypeId: 0, kind: null, kindId: 0}, setValue)
                        }
                    }
                    } errMsg={errors?.productIds?.message} attrId={"productIds"} setValue={setValue} attr={"productNames"} control={control}/>
                </Grid>
                <CategoryWatched/>
                <Grid size={6}>
                    <FormControl fullWidth={true} required={true}>
                        <FormLabel>商机阶段</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => {
                            return <Autocomplete
                                options={stage}
                                value={value as any}
                                onChange={(_, option: DictPO | null) => {
                                    setValue("stageId", option!.value)
                                    onChange(option?.label)
                                }}
                                fullWidth
                                renderInput={(params) => (
                                    <TextField error={!!errors?.stage}
                                               helperText={errors?.stage?.message}
                                               variant="outlined"
                                               placeholder={"选择商机阶段"} {...params} />
                                )}
                            />
                        }} name={"stage"}/>
                    </FormControl>
                </Grid>

                <Grid size={6}>
                    <FormControl fullWidth={true} autoFocus={true} required={true}>
                        <FormLabel htmlFor="money">商机金额</FormLabel>
                        <TextField
                            error={!!errors?.money}
                            helperText={errors?.money?.message}
                            id="money"
                            {...register("money")}
                            slotProps={{
                                input: {
                                    inputProps: {maxLength: 5},
                                    endAdornment: <CurrencyYuanOutlined color="disabled"/>,
                                },
                            }}
                            placeholder="填写商机金额"
                            variant="outlined"
                        />
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} autoFocus={true} required={true}>
                        <FormLabel>数量</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => <TextField
                            error={!!errors?.amount}
                            helperText={errors?.amount?.message}
                            value={value}
                            onChange={event => onChange(parseInt(event.target.value) || null)}
                            placeholder="填写数量"
                            variant="outlined"
                        />} name={"amount"}/>

                    </FormControl>
                </Grid>

                <Grid size={6}>
                    <FormControl fullWidth={true} autoFocus={true} required={true}>
                        <FormLabel>预计成交时间</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => <DatePickers>
                            <DatePicker
                                showYearDropdown={true}
                                showMonthDropdown={true}
                                dateFormat="yyyy/MM/dd"
                                selected={value}
                                onChange={date => onChange(date)}
                                locale='zhCN'
                                placeholderText={"选择预计成交时间"}
                                customInput={<TextField sx={{
                                    width: "100%",
                                    "& .MuiInputBase-root": {padding: "8px 8px 8px 12px"},
                                }}
                                                        error={!!errors?.dealDate}
                                                        helperText={errors?.dealDate?.message}
                                />}
                            />
                        </DatePickers>} name={"dealDate"}/>
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} autoFocus={true} required={true}>
                        <FormLabel>成交概率</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => <TextField
                            error={!!errors?.dealRate}
                            helperText={errors?.dealRate?.message}
                            type={'number'}
                            value={value}
                            onChange={event => onChange(parseInt(event.target.value))}
                            slotProps={{
                                input: {
                                    inputProps: {
                                        min: 0,
                                        max: 100,
                                        maxLength: 3,
                                        inputMode: 'numeric', pattern: '[0-9]*'
                                    },
                                    endAdornment: <ShowChartOutlined color="disabled"/>,
                                },
                            }}
                            placeholder="成交概率"
                            variant="outlined"
                        />} name={'dealRate'}/>
                    </FormControl>
                </Grid>
                <Grid size={12}>
                    <FormControl fullWidth={true}>
                        <FormLabel>备注</FormLabel>
                        <TextField
                            multiline
                            rows={3}
                            {...register("remark")}
                            slotProps={{
                                input: {
                                    sx: {height: 80},
                                    inputProps: {maxLength: 250},
                                },
                            }}
                            placeholder="输入备注"
                            fullWidth
                        />
                    </FormControl>
                </Grid>
            </Grid>
            <DrawerFooter loading={createChancePending || updateChancePending} onReset={() => {
                reset(defaultValues)
            }}/>
        </Box>
    );
};
